import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { emitEditLock, getSocket, ensureSocketAuth } from './socket'
import type { EditingDeniedPayload, EditingPayload, EditingUpdatedPayload } from './editingTypes'
import { Message } from 'rsuite'

type LockState =
    | { status: 'checking' }
    | { status: 'owned' }
    | { status: 'blocked'; lockedBy: { user_id: string; user_name?: string }; position?: number; queueLength?: number }

type UseEditLockArgs = {
    table: string
    id: string
    tabId: string
    userId: string
    userName?: string
    url?: string

    /**
     * Disable all behavior if false (e.g. newRecord screens).
     */
    enabled?: boolean

    /**
     * Called exactly when the lock transitions into "owned"
     * (i.e. you got promoted or first acquired).
     */
    onAcquire?: () => void | Promise<void>
    onUpdated?: (payload: EditingUpdatedPayload) => void
}

export const useEditLock = ({
    table,
    id,
    tabId,
    userId,
    userName,
    url,
    enabled = true,
    onAcquire,
    onUpdated,
}: UseEditLockArgs) => {
    const { t } = useTranslation()
    const socket = useMemo(() => getSocket(), [])
    const [lock, setLock] = useState<LockState>({ status: 'checking' })
    const prevStatus = useRef<LockState['status']>('checking')
    const onAcquireRef = useRef<UseEditLockArgs['onAcquire']>(onAcquire)
    const onUpdatedRef = useRef<UseEditLockArgs['onUpdated']>(onUpdated)

    useEffect(() => {
        onAcquireRef.current = onAcquire
    }, [onAcquire])

    useEffect(() => {
        onUpdatedRef.current = onUpdated
    }, [onUpdated])

    useEffect(() => {
        if (!enabled || !table || !id || !userId) return

        const onDenied = (msg: EditingDeniedPayload & { position?: number; queueLength?: number }) => {
            if (msg.table !== table || msg.id !== id) return
            setLock({
                status: 'blocked',
                lockedBy: msg.lockedBy,
                position: msg.position,
                queueLength: msg.queueLength,
            })
        }

        const onSetEditing = (msg: EditingPayload) => {
            if (msg.table !== table || msg.id !== id) return

            if (msg.mode === 'close') {
                // Resource is temporarily unowned; we'll remain "checking"
                setLock({ status: 'checking' })
                return
            }

            // mode === 'open'
            if (String(msg.user_id) === String(userId)) {
                setLock({ status: 'owned' })
            } else {
                setLock({ status: 'blocked', lockedBy: { user_id: msg.user_id, user_name: msg.user_name } })
            }
        }

        const onGetEditings = (payload: { editings: EditingPayload[] }) => {
            const found = payload.editings.find((e) => e.table === table && e.id === id)
            if (!found) {
                // The server sends this list on every (re)connect, and right
                // after it the request for this lock again (see lockKeeper) —
                // its answer decides. Dropping to 'checking' meanwhile would
                // turn the way back to 'owned' into an onAcquire, and that
                // reloads the form over whatever was typed but not yet saved.
                return
            }

            if (String(found.user_id) === String(userId)) setLock({ status: 'owned' })
            else setLock({ status: 'blocked', lockedBy: { user_id: found.user_id, user_name: found.user_name } })
        }

        socket.on('editing server: denied', onDenied)
        socket.on('editing server: setEditing', onSetEditing)
        socket.on('editing server: getEditings', onGetEditings)

        // join + open (this will either acquire or enqueue)
        emitEditLock({
            table,
            url,
            id,
            tabId,
            mode: 'open',
            user_id: userId,
            user_name: userName,
        } satisfies EditingPayload)

        const onResourceUpdated = (msg: EditingUpdatedPayload) => {
            if (msg.table !== table || msg.id !== id) return
            if (String(msg.by.user_id) === String(userId)) return // ignore own saves
            onUpdatedRef.current?.(msg)
        }

        socket.on('editing server: resourceUpdated', onResourceUpdated)

        return () => {
            emitEditLock({
                table,
                url,
                id,
                tabId,
                mode: 'close',
                user_id: userId,
                user_name: userName,
            } satisfies EditingPayload)

            socket.off('editing server: denied', onDenied)
            socket.off('editing server: setEditing', onSetEditing)
            socket.off('editing server: getEditings', onGetEditings)
            socket.off('editing server: resourceUpdated', onResourceUpdated)
        }
    }, [socket, enabled, table, id, tabId, userId, userName, url])

    // Fire onAcquire on transition -> owned
    useEffect(() => {
        if (!enabled) return
        const curr = lock.status
        const prev = prevStatus.current
        prevStatus.current = curr

        if (curr === 'owned' && prev !== 'owned') {
            void onAcquireRef.current?.()
        }
    }, [enabled, lock.status])

    const afterSave = () => {
        if (!enabled || !table || !id || !tabId || !userId) return

        // Release current turn -> next user is promoted
        // Then immediately request again -> you go to BACK of the queue
        emitEditLock({
            table,
            url,
            id,
            tabId,
            mode: 'close',
            user_id: userId,
            user_name: userName,
        } satisfies EditingPayload)

        emitEditLock({
            table,
            url,
            id,
            tabId,
            mode: 'open',
            user_id: userId,
            user_name: userName,
        } satisfies EditingPayload)
    }
    const banner =
        lock.status === 'blocked' ? (
            <div style={{ marginBottom: '1em' }}>
                <Message type="error" showIcon>
                    {t('Locked: {{name}} is editing this record.', {
                        name: lock.lockedBy.user_name || lock.lockedBy.user_id,
                    })}
                </Message>
            </div>
        ) : null

    return {
        ...lock,
        banner,
        // afterSave,
    }
}
